/* Modulo: Stats Cards — scroll reveal + count-up + mobile slider dots.
   Vanilla IIFE. Injected sections never load the extension's `moduloLazyInit`
   (it has no definition anywhere in the repo and modulo-core.js is not on disk),
   so this rolls its own IntersectionObserver rather than carrying a dead branch.

   Contract with the CSS: this script NEVER writes a bar width. The resting DOM
   already holds the true percentage; JS only adds `.is-in` to opt into the
   animation. A dead or throttled script therefore degrades to the correct
   figure, never to an honest-looking 0%. */
(function () {
  'use strict';

  var SELECTOR = '[data-modulo-sc]';
  var DURATION = 1100;
  var STAGGER = 90;

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ── count-up ───────────────────────────────────────────────
     Integer interpolation only. `percent` is a schema range 0-100, so there is
     no string to parse — which is why a European "77,5" can never reach here
     and silently become 77 the way a free-text value would. */
  function countUp(nums) {
    // Shared kill switch. The failsafe below can fire while a rAF loop is merely
    // PAUSED (background tab) rather than dead; without this flag that loop
    // resumes afterwards and writes an interpolated value back over the corrected
    // one, visibly counting down before settling again.
    var settled = false;
    var i;

    for (i = 0; i < nums.length; i++) {
      (function (el, delay) {
        var target = parseInt(el.getAttribute('data-sc-pct'), 10);
        if (isNaN(target)) return;
        el.textContent = '0';
        var start = null;
        function frame(now) {
          if (settled) return;
          if (start === null) start = now;
          var t = (now - start - delay) / DURATION;
          if (t < 0) { requestAnimationFrame(frame); return; }
          // Land on the target directly instead of rounding an eased value, so
          // the final frame can never settle one short.
          if (t >= 1) { el.textContent = String(target); return; }
          el.textContent = String(Math.round(target * (1 - Math.pow(1 - t, 3))));
          requestAnimationFrame(frame);
        }
        requestAnimationFrame(frame);
      })(nums[i], i * STAGGER);
    }

    // Failsafe: rAF is paused in background tabs and absent in some headless and
    // print pipelines. Without this a numeral could be stranded at "0".
    setTimeout(function () {
      settled = true;
      for (var j = 0; j < nums.length; j++) {
        nums[j].textContent = nums[j].getAttribute('data-sc-pct');
      }
    }, DURATION + nums.length * STAGGER + 500);
  }

  /* ── reveal ── */
  function wireReveal(root) {
    var animateBars = root.getAttribute('data-sc-animate-bars') === 'true';
    var animateNums = root.getAttribute('data-sc-animate-numbers') === 'true';
    if (!animateBars && !animateNums) return;

    var grid = root.querySelector('.modulo-sc__grid');
    var cards = root.querySelectorAll('.modulo-sc__card');
    var nums = root.querySelectorAll('.modulo-sc__num');
    if (!grid) return;

    // Under reduced motion we opt into nothing at all. The resting state is
    // already correct, so there is no final value to snap back to.
    if (reducedMotion()) return;

    function reveal() {
      if (animateBars) {
        for (var i = 0; i < cards.length; i++) cards[i].classList.add('is-in');
      }
      if (animateNums) countUp(nums);
    }

    if (!('IntersectionObserver' in window)) { reveal(); return; }

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          io.disconnect();
          reveal();
          return;
        }
      }
    }, { rootMargin: '0px 0px -12% 0px' });
    io.observe(grid);
  }

  /* ── mobile slider dots ── */
  function wireDots(root) {
    var grid = root.querySelector('[data-sc-grid]');
    var dotWrap = root.querySelector('[data-sc-dots]');
    if (!grid || !dotWrap) return;

    var cards = grid.querySelectorAll('.modulo-sc__card');
    var dots = dotWrap.querySelectorAll('[data-sc-dot]');
    if (!cards.length || !dots.length) return;

    var rtl = getComputedStyle(grid).direction === 'rtl';

    function setActive(index) {
      for (var i = 0; i < dots.length; i++) {
        if (i === index) dots[i].setAttribute('aria-current', 'true');
        else dots[i].removeAttribute('aria-current');
      }
    }

    for (var d = 0; d < dots.length; d++) {
      (function (btn, index) {
        btn.addEventListener('click', function () {
          // scrollIntoView is direction-aware, unlike a manual scrollLeft write
          // (translateX/scrollLeft arithmetic silently mirrors under RTL).
          cards[index].scrollIntoView({
            behavior: reducedMotion() ? 'auto' : 'smooth',
            inline: 'start',
            block: 'nearest'
          });
          setActive(index);
        });
      })(dots[d], d);
    }

    var raf = null;
    grid.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;

        // At the scroll extreme, snap the readout to the last card. On a peek
        // rail the trailing card's leading edge never reaches the rail's own
        // leading edge, so a nearest-edge match alone can never select it.
        if (grid.scrollWidth - grid.clientWidth - Math.abs(grid.scrollLeft) <= 2) {
          setActive(cards.length - 1);
          return;
        }

        var gridRect = grid.getBoundingClientRect();
        var anchor = rtl ? gridRect.right : gridRect.left;
        var best = 0;
        var bestDist = Infinity;
        for (var i = 0; i < cards.length; i++) {
          var r = cards[i].getBoundingClientRect();
          var dist = Math.abs((rtl ? r.right : r.left) - anchor);
          if (dist < bestDist) { bestDist = dist; best = i; }
        }
        setActive(best);
      });
    }, { passive: true });
  }

  function init(root) {
    if (root.hasAttribute('data-sc-ready')) return;
    root.setAttribute('data-sc-ready', 'true');
    wireReveal(root);
    wireDots(root);
  }

  function boot(scope) {
    var nodes = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Two instances of this section on one page means two <script src> tags, and
  // an identical src is still executed twice — so the document-level listener
  // would accumulate a duplicate per instance without this guard.
  if (!window.__moduloScBound) {
    window.__moduloScBound = true;
    // Theme editor re-renders the section on every setting change.
    document.addEventListener('shopify:section:load', function (e) {
      boot(e.target || document);
    });
  }
})();
